<template>
  <div class="counter">
    <UiInput
      ref="input"
      :model-value="modelValue"
      :hide-details="true"
      :class="{ 'counter-input--large': large }"
      :readonly="readonly"
      :select-on-focus="true"
      rounded="lg"
      density="compact"
      type="number"
      inputmode="numeric"
      data-test="input-counter"
      class="counter-input"
      @update:model-value="handleChange"
      @blur="$emit('blur')"
    />
    <div v-show="showArrows" class="counter-icons">
      <UiIcon
        icon="$triangleTop"
        color="grey-400"
        size="12"
        class="icon"
        @click="increase"
      />
      <UiIcon
        icon="$triangleDown"
        color="grey-400"
        size="12"
        class="icon"
        @click="decrease"
      />
    </div>
  </div>
  <UiText
    v-if="hint"
    tag="span"
    variant="body-4"
    color="grey-600"
    class="text-right d-block px-2"
  >
    {{ hint }}
  </UiText>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { UiInput, UiIcon, UiText } from '~/ui';

export interface Props {
  modelValue: number | string;
  min?: number;
  large?: boolean;
  hint?: string;
  showArrows?: boolean;
  readonly?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  large: false,
  min: 1,
  hint: undefined,
  showArrows: true,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'blur'): void;
}>();

const input = ref();

function focus(): void {
  input.value.focus();
}

defineExpose({
  focus,
});

const increase = (): void => {
  const currentValue = +props.modelValue;
  if (!isNaN(currentValue)) {
    emit('update:modelValue', (currentValue + 1).toString());
  }
};

const decrease = (): void => {
  const currentValue = +props.modelValue;

  if (!isNaN(currentValue)) {
    const newValue = currentValue - 1;
    if (props.min != null && newValue < props.min) return;
    emit('update:modelValue', newValue.toString());
  }
};

const handleChange = (value: string): void => {
  if (props.min != null && +value < props.min) return;

  const validatedValue = value.replace(/^0+|\.+|e/g, '');

  if (!isNaN(Number(validatedValue))) {
    emit('update:modelValue', validatedValue);
  }
};
</script>

<style lang="scss" scoped>
.counter {
  position: relative;
  width: 100%;

  :deep(.v-field.v-field--appended) {
    --v-field-padding-end: 12px;
  }

  .counter-icons {
    position: absolute;
    display: flex;
    flex-direction: column;
    top: 0;
    right: 5px;
    height: 100%;
    justify-content: center;
  }

  .icon {
    transition: all 0.2s;

    &:hover {
      color: #c4daf3 !important;
    }
  }
}

:deep(.v-text-field input) {
  text-align: right;
}

:deep(.v-field__outline) {
  .v-field__outline__end {
    border-radius: 0 12px 12px 0;
  }
}
</style>
